import { ENDPOINTS } from 'config/endpoints';
import { useAuth } from 'context/auth';
import { invalidateEditProspectQueries } from 'hooks/services/prospect/useEditProspect';
import {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useConditionalSnackBar } from '../snackbar';
import { invalidateEditAccountQueries } from 'hooks/services/account/useEditAccount';
import useGetActiveAiVariableAnalytics from 'hooks/services/assistant/useGetActiveAiVariableAnalytics';
import { AiVariablePollerContext } from './contexts';

const AiVariablePollerProvider: FC<PropsWithChildren> = ({ children }) => {
  const { enabledContextCalls } = useAuth();

  const [totalToShowProspects, setTotalToShowProspects] = useState<number>(1);
  const [prospectsEnrichmentOngoing, setProspectsEnrichmentOngoing] =
    useState(false);
  const [prospectsEnrichmentCompleted, setProspectsEnrichmentCompleted] =
    useState(false);

  const [totalToShowLeads, setTotalToShowLeads] = useState<number>(1);
  const [leadsEnrichmentOngoing, setLeadsEnrichmentOngoing] = useState(false);
  const [leadsEnrichmentCompleted, setLeadsEnrichmentCompleted] =
    useState(false);

  const [totalToShowCompanies, setTotalToShowCompanies] = useState<number>(1);
  const [companiesEnrichmentOngoing, setCompaniesEnrichmentOngoing] =
    useState(false);
  const [companiesEnrichmentCompleted, setCompaniesEnrichmentCompleted] =
    useState(false);

  const {
    data: activeAiVariableAnalytics,
    refetch: refetchGetActiveAiVariableAnalytics,
    error,
  } = useGetActiveAiVariableAnalytics(enabledContextCalls);

  const queryClient = useQueryClient();
  useEffect(() => {
    let t: NodeJS.Timeout;

    const prospectScrapeAnalytics =
      activeAiVariableAnalytics?.data?.data?.prospects;
    const prospectsLeftToEnrich = prospectScrapeAnalytics
      ? prospectScrapeAnalytics.total - prospectScrapeAnalytics.completed
      : undefined;

    setProspectsEnrichmentOngoing(!!prospectsLeftToEnrich);
    if (prospectScrapeAnalytics && prospectScrapeAnalytics?.total > 0) {
      setTotalToShowProspects(prospectScrapeAnalytics.total);
    }

    const leadScrapeAnalytics = activeAiVariableAnalytics?.data?.data?.leads;
    const leadsLeftToEnrich = leadScrapeAnalytics
      ? leadScrapeAnalytics.total - leadScrapeAnalytics.completed
      : undefined;

    setLeadsEnrichmentOngoing(!!leadsLeftToEnrich);
    if (leadScrapeAnalytics && leadScrapeAnalytics?.total > 0) {
      setTotalToShowLeads(leadScrapeAnalytics.total);
    }

    const companyScrapeAnalytics =
      activeAiVariableAnalytics?.data?.data?.accounts;
    const companiesLeftToEnrich = companyScrapeAnalytics
      ? companyScrapeAnalytics.total - companyScrapeAnalytics.completed
      : undefined;

    setCompaniesEnrichmentOngoing(!!companiesLeftToEnrich);
    if (companyScrapeAnalytics && companyScrapeAnalytics?.total > 0) {
      setTotalToShowCompanies(companyScrapeAnalytics.total);
    }

    let hasToInvalidateProspectSearches = false;
    let hasToInvalidateAccountSearches = false;

    if (prospectsLeftToEnrich || companiesLeftToEnrich || leadsLeftToEnrich) {
      if (prospectsLeftToEnrich || leadsLeftToEnrich) {
        hasToInvalidateProspectSearches = true;
      }
      if (companiesLeftToEnrich) {
        hasToInvalidateAccountSearches = true;
      }
      t = setTimeout(() => {
        refetchGetActiveAiVariableAnalytics();
      }, 3000);
    }

    if (!prospectsLeftToEnrich && prospectsEnrichmentOngoing) {
      hasToInvalidateProspectSearches = true;
      setProspectsEnrichmentCompleted(true);
      setTimeout(() => {
        setProspectsEnrichmentCompleted(false);
      }, 2000);
    }

    if (!leadsLeftToEnrich && leadsEnrichmentOngoing) {
      hasToInvalidateProspectSearches = true;

      setLeadsEnrichmentCompleted(true);
      setTimeout(() => {
        setLeadsEnrichmentCompleted(false);
      }, 2000);
    }

    if (!companiesLeftToEnrich && companiesEnrichmentOngoing) {
      hasToInvalidateAccountSearches = true;

      setCompaniesEnrichmentCompleted(true);
      setTimeout(() => {
        setCompaniesEnrichmentCompleted(false);
      }, 2000);
    }

    if (hasToInvalidateProspectSearches || hasToInvalidateAccountSearches) {
      if (hasToInvalidateProspectSearches) {
        invalidateEditProspectQueries(queryClient);
      }

      if (hasToInvalidateAccountSearches) {
        invalidateEditAccountQueries(queryClient, true);
      }

      queryClient.invalidateQueries([ENDPOINTS.user.credits]);
    }

    return () => {
      clearTimeout(t);
    };
  }, [activeAiVariableAnalytics]);

  useConditionalSnackBar([
    {
      condition: !error && companiesEnrichmentCompleted,
      message: `${totalToShowCompanies} ${totalToShowCompanies === 1 ? 'company' : 'companies'} enrichment successfully completed`,
      severity: 'success',
    },
    {
      condition: !error && prospectsEnrichmentCompleted,
      message: `${totalToShowProspects} ${totalToShowProspects === 1 ? 'contact' : 'contacts'} enrichment successfully completed`,
      severity: 'success',
    },
    {
      condition: !error && leadsEnrichmentCompleted,
      message: `${totalToShowLeads} ${totalToShowLeads === 1 ? 'lead' : 'leads'} enrichment successfully completed`,
      severity: 'success',
    },
  ]);

  const contextValue = useMemo(
    () => ({
      enrichingAiVariableProspect: prospectsEnrichmentOngoing,
      enrichingAiVariableLead: leadsEnrichmentOngoing,
      enrichingAiVariableCompany: companiesEnrichmentOngoing,
    }),
    [
      prospectsEnrichmentOngoing,
      leadsEnrichmentOngoing,
      companiesEnrichmentOngoing,
    ],
  );

  return (
    <AiVariablePollerContext.Provider value={contextValue}>
      {children}
    </AiVariablePollerContext.Provider>
  );
};

export default AiVariablePollerProvider;

export function useAiVariablePollerContext() {
  const context = useContext(AiVariablePollerContext);
  if (context === undefined) {
    throw new Error(
      `useAiVariablePoller must be used within a AiVariablePollerProvider`,
    );
  }

  return context;
}
